export default class KeyboardManager {
  private static _instance: KeyboardManager;
  private isKeyboardOpen = false;
  private initialViewportHeight: number = 0;
  private isIOS = false;
  private isAndroid = false;
  private boundOnViewportResize: (() => void) | null = null;
  private boundOnFocusIn: (() => void) | null = null;
  private boundOnFocusOut: (() => void) | null = null;

  static getInstance() {
    if (!this._instance) {
      this._instance = new KeyboardManager();
    }
    return this._instance;
  }

  constructor() {}

  start() {
    const ua = navigator.userAgent;
    // iPadOS 13+ reports a desktop Mac UA; the touch-point count tells it apart.
    this.isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    this.isAndroid = /Android/i.test(ua);

    if (!this.isIOS && !this.isAndroid) {
      return;
    }

    // Focus is the signal that always works: visualViewport inside the
    // cross-origin widget iframe is unreliable on iOS (its keyboard resize
    // events often never reach the frame — see AgentHomeV4's composer lift),
    // so it must not be the ONLY source of the keyboard state.
    this.boundOnFocusIn = this.onFocusIn.bind(this);
    this.boundOnFocusOut = this.onFocusOut.bind(this);
    document.addEventListener("focusin", this.boundOnFocusIn);
    document.addEventListener("focusout", this.boundOnFocusOut);

    if (this.isIOS && window.visualViewport) {
      // Additional iOS signal for the one case focus can't see: dismissing
      // the keyboard without blurring (iPad's keyboard-collapse key). When
      // the event does fire, it corrects the state in both directions.
      this.initialViewportHeight = window.visualViewport.height;
      this.boundOnViewportResize = this.onViewportResize.bind(this);
      window.visualViewport.addEventListener(
        "resize",
        this.boundOnViewportResize
      );
    }
  }

  stop() {
    if (window.visualViewport && this.boundOnViewportResize) {
      window.visualViewport.removeEventListener(
        "resize",
        this.boundOnViewportResize
      );
    }
    if (this.boundOnFocusIn) {
      document.removeEventListener("focusin", this.boundOnFocusIn);
    }
    if (this.boundOnFocusOut) {
      document.removeEventListener("focusout", this.boundOnFocusOut);
    }
  }

  private onViewportResize() {
    if (!window.visualViewport) {
      return;
    }

    const currentHeight = window.visualViewport.height;
    const keyboardOpen =
      currentHeight < this.initialViewportHeight * 0.75;

    if (keyboardOpen !== this.isKeyboardOpen) {
      this.isKeyboardOpen = keyboardOpen;
      this.updateCSSClass();
    }
  }

  private onFocusIn() {
    const el = document.activeElement;
    if (this.isEditableElement(el)) {
      this.isKeyboardOpen = true;
      this.updateCSSClass();
    }
  }

  private onFocusOut() {
    this.isKeyboardOpen = false;
    this.updateCSSClass();
  }

  private isEditableElement(el: Element | null): boolean {
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT") {
      const type = (el as HTMLInputElement).type;
      return !["button", "submit", "reset", "checkbox", "radio", "file", "image", "hidden"].includes(type);
    }
    if (tag === "TEXTAREA") return true;
    if ((el as HTMLElement).isContentEditable) return true;
    return false;
  }

  private updateCSSClass() {
    const platformClass = this.isIOS
      ? "gleap-keyboard-open--ios"
      : "gleap-keyboard-open--android";

    if (this.isKeyboardOpen) {
      document.body.classList.add("gleap-keyboard-open");
      document.body.classList.add(platformClass);
    } else {
      document.body.classList.remove("gleap-keyboard-open");
      document.body.classList.remove("gleap-keyboard-open--ios");
      document.body.classList.remove("gleap-keyboard-open--android");
    }
  }
}
